import React, { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Steps } from 'react-vant'
import CustomButton from '@/components/CustomButton'
import PaymentMethods from '../../components/PaymentMethods'
import { Toast } from 'react-vant'
import { usePhysicalCardInfo } from '@/hooks'
import './index.css'

// 步骤状态类型
interface StepItem {
    title: string
    date: string
    description: string
    status: 'finish' | 'process' | 'wait'
}

export default React.memo(function PhysicalCardStatus() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { cardInfo: physicalCardInfo } = usePhysicalCardInfo()

    const handleActivate = () => {
        Toast.info({
            message: t('卡片审核中请耐心等待'),
            duration: 1000
        })
        // 暂时不跳转到激活页面
        // navigate('/card/activate-card')
    }

    // 格式化日期为 YYYY-MM-DD 格式
    const formatDate = useCallback((dateStr: string | undefined): string => {
        if (!dateStr) return ''
        try {
            // 尝试解析日期字符串
            const date = new Date(dateStr)
            if (isNaN(date.getTime())) return ''
            
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        } catch (error) {
            console.error('日期格式化失败:', error)
            return ''
        }
    }, [])

    // 计算未来日期
    const addDays = useCallback((dateStr: string | undefined, days: number): string => {
        if (!dateStr) return ''
        try {
            const date = new Date(dateStr)
            if (isNaN(date.getTime())) return ''
            
            date.setDate(date.getDate() + days)
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        } catch (error) {
            console.error('日期计算失败:', error)
            return ''
        }
    }, [])

    // 步骤数据
    const steps = useMemo<StepItem[]>(() => {
        const createdDate = physicalCardInfo?.created_at_string
        
        return [
            {
                title: t('申核中'),
                date: formatDate(createdDate),
                description: t('通常这个过程会在个工作日内完成', { days: 1 }),
                status: 'wait'
            },
            {
                title: t('制卡中'),
                date: addDays(createdDate, 1),
                description: t('通常这个过程会在个工作日内完成', { days: 7 }),
                status: 'wait'
            },
            {
                title: t('邮寄中'),
                date: addDays(createdDate, 8),
                description: t('通常这个过程会在个工作日内完成', { days: '7-14' }),
                status: 'wait'
            },
            {
                title: t('激活中'),
                date: addDays(createdDate, 15),
                description: t('通常这个过程会在个工作日内完成', { days: 1 }),
                status: 'wait'
            }
        ]
    }, [t, physicalCardInfo?.created_at_string, formatDate, addDays])

    // 当前激活的步骤索引
    const activeStep = useMemo(() => {
        return steps.findIndex(step => step.status === 'wait')
    }, [steps])

    return (
        <div className="px-6 pt-6 pb-24">
            {/* 卡片预览 */}
            <div className="mb-6 flex items-center justify-center">
                <img
                    className="w-full"
                    src="/images/card/wpay-physical.png"
                />
            </div>

            {/* 卡片名称 */}
            <div className="text-left mt-6 mb-3">
                <div className="text-6.5 font-semibold text-black">{t('环球精英实体卡')}</div>
            </div>


            {/* 支付方式图标 */}
            <div className="mb-3 flex justify-start">
                <PaymentMethods />
            </div>

            {/* 步骤条 */}
            <div className="physical-card-steps mb-8">
                <Steps direction="vertical" active={activeStep} inactiveIcon={<div><div className="w-4 h-4 rounded-full bg-[#EDEDED]"></div></div>} finishIcon={<div><div className="w-4 h-4 rounded-full bg-[var(--primary-color)]"></div></div>} activeIcon={<div><div className="w-4 h-4 rounded-full bg-[var(--primary-color)]"></div></div>} activeColor="var(--primary-color)">
                    {steps.map((step, index) => (
                        <Steps.Item key={index}>
                            <div className="step-content">
                                <div className="flex items-center mb-1">
                                    <span className={`text-3.5 mr-2 font-bold ${step.status === 'process' ? 'text-[var(--primary-color)]' : step.status === 'finish' ? 'text-black' : 'text-black/60' }`}>
                                        {step.title}
                                    </span>
                                    <span className="text-3 text-[#808080]">{step.date}</span>
                                </div>
                                <div className="text-3 text-[#000000]/30 leading-relaxed mt-3">
                                    {step.description}
                                </div>
                            </div>
                        </Steps.Item>
                    ))}
                </Steps>
            </div>

            {/* 激活卡片按钮 */}
            <CustomButton onClick={handleActivate}>
                {t('激活卡片')}
            </CustomButton>
        </div>
    )
})
